/**
 * AuroraBackground Component
 *
 * Northern lights background effect for Remotion.
 * Remotion-native version of shadcn/ui aurora-background component.
 *
 * Features:
 * - Smooth aurora animation using frame-based calculations
 * - Mesmerizing northern lights effect
 * - Radial gradient masking for focus
 * - GPU-optimized transforms
 * - Configurable colors and animation speed
 */

import React from 'react';
import { useCurrentFrame, useVideoConfig, AbsoluteFill, interpolate } from 'remotion';

export interface AuroraBackgroundProps {
  /** Children to render on top of aurora */
  children?: React.ReactNode;
  /** Apply radial gradient masking for focus */
  showRadialGradient?: boolean;
  /** Aurora color palette */
  colors?: {
    primary?: string;
    secondary?: string;
    tertiary?: string;
    accent?: string;
  };
  /** Animation speed multiplier (1 = normal, 0.5 = half speed) */
  animationSpeed?: number;
  /** Base background color */
  backgroundColor?: string;
  /** Blur amount for aurora effect */
  blurAmount?: number;
  /** Additional className */
  className?: string;
}

export const AuroraBackground: React.FC<AuroraBackgroundProps> = ({
  children,
  showRadialGradient = true,
  colors = {},
  animationSpeed = 1,
  backgroundColor = '#0c0c0c',
  blurAmount = 60,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Default aurora colors (northern lights palette)
  const {
    primary = 'rgba(59, 130, 246, 0.5)',    // Blue
    secondary = 'rgba(139, 92, 246, 0.5)',   // Purple
    tertiary = 'rgba(16, 185, 129, 0.4)',    // Green/Teal
    accent = 'rgba(236, 72, 153, 0.3)',      // Pink
  } = colors;

  // Animate background position over time
  // Original CSS: from 50% 50% to 350% 50% over 60 seconds
  // Calculate from fps for robustness across different frame rates
  const cycleDuration = (60 * fps) / animationSpeed;
  const progress = (frame % cycleDuration) / cycleDuration;

  // Calculate background position (50% to 350% = 300% range)
  const backgroundPositionX = interpolate(
    progress,
    [0, 1],
    [50, 350],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Create subtle wave motion for organic feel
  const waveOffset1 = Math.sin(frame * 0.02 * animationSpeed) * 10;
  const waveOffset2 = Math.cos(frame * 0.015 * animationSpeed) * 15;
  const waveOffset3 = Math.sin(frame * 0.025 * animationSpeed) * 8;

  // Aurora gradient layers
  const auroraGradient = `
    repeating-linear-gradient(
      100deg,
      ${primary} 10%,
      ${secondary} 15%,
      ${primary} 20%,
      ${tertiary} 25%,
      ${secondary} 30%
    ),
    repeating-linear-gradient(
      100deg,
      ${primary} 10%,
      ${secondary} 15%,
      ${primary} 20%,
      ${tertiary} 25%,
      ${secondary} 30%
    )
  `;

  // Radial gradient for focus effect (subtle fade at edges only)
  const radialMask = showRadialGradient
    ? `radial-gradient(ellipse at center, transparent 0%, transparent 60%, ${backgroundColor}80 85%, ${backgroundColor} 100%)`
    : 'none';

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        overflow: 'hidden',
      }}
    >
      {/* Aurora effect layer */}
      <AbsoluteFill
        style={{
          background: auroraGradient,
          backgroundSize: '300% 200%',
          backgroundPosition: `${backgroundPositionX}% ${50 + waveOffset1}%, ${backgroundPositionX + 50}% ${50 + waveOffset2}%`,
          filter: `blur(${blurAmount}px)`,
          opacity: 0.85,
          transform: `translateY(${waveOffset3}px)`,
          willChange: 'background-position, transform',
        }}
      />

      {/* Secondary aurora layer for depth */}
      <AbsoluteFill
        style={{
          background: `
            radial-gradient(
              ellipse 80% 50% at 50% -20%,
              ${primary} 0%,
              transparent 50%
            ),
            radial-gradient(
              ellipse 60% 40% at 30% 100%,
              ${tertiary} 0%,
              transparent 50%
            ),
            radial-gradient(
              ellipse 50% 30% at 80% 80%,
              ${accent} 0%,
              transparent 50%
            )
          `,
          opacity: 0.7,
          filter: `blur(${blurAmount * 0.8}px)`,
          transform: `scale(1.1) translateX(${waveOffset1}px) translateY(${waveOffset2}px)`,
        }}
      />

      {/* Radial gradient mask for focus */}
      {showRadialGradient && (
        <AbsoluteFill
          style={{
            background: radialMask,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Subtle noise overlay for texture */}
      <AbsoluteFill
        style={{
          opacity: 0.015,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
        }}
      />

      {/* Children content */}
      <AbsoluteFill
        style={{
          zIndex: 10,
        }}
      >
        {children}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

/**
 * Preset aurora color themes
 */
export const AURORA_PRESETS = {
  /** Classic northern lights - blue, purple, green */
  classic: {
    primary: 'rgba(59, 130, 246, 0.5)',
    secondary: 'rgba(139, 92, 246, 0.5)',
    tertiary: 'rgba(16, 185, 129, 0.4)',
    accent: 'rgba(236, 72, 153, 0.3)',
  },
  /** Warm sunset aurora */
  sunset: {
    primary: 'rgba(251, 146, 60, 0.5)',
    secondary: 'rgba(244, 63, 94, 0.5)',
    tertiary: 'rgba(234, 179, 8, 0.4)',
    accent: 'rgba(239, 68, 68, 0.3)',
  },
  /** Ocean/teal aurora */
  ocean: {
    primary: 'rgba(20, 184, 166, 0.5)',
    secondary: 'rgba(6, 182, 212, 0.5)',
    tertiary: 'rgba(34, 211, 238, 0.4)',
    accent: 'rgba(59, 130, 246, 0.3)',
  },
  /** Luxury gold/purple */
  luxury: {
    primary: 'rgba(217, 119, 6, 0.4)',
    secondary: 'rgba(139, 92, 246, 0.4)',
    tertiary: 'rgba(251, 191, 36, 0.3)',
    accent: 'rgba(168, 85, 247, 0.3)',
  },
  /** Minimal grayscale */
  minimal: {
    primary: 'rgba(148, 163, 184, 0.3)',
    secondary: 'rgba(100, 116, 139, 0.3)',
    tertiary: 'rgba(71, 85, 105, 0.2)',
    accent: 'rgba(51, 65, 85, 0.2)',
  },
} as const;

export type AuroraPreset = keyof typeof AURORA_PRESETS;

export default AuroraBackground;
